/**
 * Shared types for map data structures
 */

/** Tile size in pixels */
export const TILE_SIZE = 32;

/** Maximum map dimensions */
export const MAX_MAP_SIZE = 1000;

/** Tile flags */
export enum TileFlags {
    NONE = 0,
    BLOCKED = 0x80,
    TELEPORT = 0x40,
    FARMING = 0x20,
    BUILDABLE = 0x10,
}

/**
 * Single tile data structure
 * Matches the legacy format: 10 bytes per tile
 */
export interface TileData {
    /** Sprite ID for the base tile (e.g., maptiles1 = 0-31) */
    tileSprite: number;
    /** Frame index within the tile sprite */
    tileSpriteFrame: number;
    /** Sprite ID for the object layer (trees, objects) */
    objectSprite: number;
    /** Frame index within the object sprite */
    objectSpriteFrame: number;
    /** Whether movement is blocked */
    blocked: boolean;
    /** Whether this tile is a teleport point */
    teleport: boolean;
    /** Whether this tile is farmable */
    farming: boolean;
    /** Whether building is allowed */
    buildable: boolean;
}

/**
 * Create an empty tile with default values
 */
export function createEmptyTile(): TileData {
    return {
        tileSprite: 0,
        tileSpriteFrame: 0,
        objectSprite: 0,
        objectSpriteFrame: 0,
        blocked: false,
        teleport: false,
        farming: false,
        buildable: false,
    };
}

/**
 * Map document containing all tile data
 */
export interface MapDocument {
    /** Map width in tiles */
    width: number;
    /** Map height in tiles */
    height: number;
    /** 2D array of tile data [x][y] */
    tiles: TileData[][];
    /** Original filename (if loaded from file) */
    filename?: string;
    /** Whether the map has unsaved changes */
    dirty: boolean;
}

/**
 * Create a new empty map with the specified dimensions
 */
export function createEmptyMap(width: number, height: number): MapDocument {
    const tiles: TileData[][] = [];
    for (let x = 0; x < width; x++) {
        tiles[x] = [];
        for (let y = 0; y < height; y++) {
            tiles[x][y] = createEmptyTile();
        }
    }
    return {
        width,
        height,
        tiles,
        dirty: false,
    };
}

/**
 * Terrain presets for new maps
 */
export interface TerrainPreset {
    name: string;
    /** Base tile sprite ID */
    tileSprite: number;
    /** Base tile frame */
    tileFrame: number;
}

export const TERRAIN_PRESETS: TerrainPreset[] = [
    { name: 'Grass', tileSprite: 0, tileFrame: 0 },
    { name: 'Dirt', tileSprite: 0, tileFrame: 41 },
    { name: 'Stone', tileSprite: 300, tileFrame: 0 },
    { name: 'Sand', tileSprite: 330, tileFrame: 0 },
    { name: 'Snow', tileSprite: 320, tileFrame: 0 },
    { name: 'Water', tileSprite: 349, tileFrame: 0 },
];
